import React from 'react';
import { Check, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { ApprovalIconButton } from '../shared/approval';

interface WizardSidebarResponsiveProps {
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (step: number) => void;
}

const steps = [
  { number: 1, title: 'Basic Details', description: 'Stage name & approver' },
  { number: 2, title: 'Smart Rules', description: 'Routing & automation' },
  { number: 3, title: 'Notifications', description: 'Email templates' },
];

export const WizardSidebarResponsive: React.FC<WizardSidebarResponsiveProps> = ({
  currentStep,
  completedSteps,
  onStepClick,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);
  const toggleMobile = () => setIsMobileOpen(!isMobileOpen);

  const StepContent = ({ step }: { step: typeof steps[0] }) => {
    const isActive = currentStep === step.number;
    const isCompleted = completedSteps.has(step.number);
    const isAccessible = step.number === 1 || completedSteps.has(step.number - 1);

    return (
      <button
        onClick={() => {
          if (isAccessible) {
            onStepClick(step.number);
            setIsMobileOpen(false);
          }
        }}
        disabled={!isAccessible}
        className={`w-full text-left transition-all duration-200 rounded-lg ${
          isActive
            ? 'bg-primary/10 border-primary shadow-sm scale-[1.02]'
            : isCompleted
            ? 'bg-success/5 border-success/30 hover:bg-success/10'
            : 'bg-card border-border hover:bg-accent/50'
        } ${
          !isAccessible ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'
        } border p-2.5 group`}
      >
        <div className="flex items-start gap-2.5">
          {/* Step Number/Check Icon */}
          <div
            className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 ${
              isActive
                ? 'bg-primary text-primary-foreground shadow-md'
                : isCompleted
                ? 'bg-success text-success-foreground'
                : 'bg-muted text-muted-foreground group-hover:bg-muted-foreground/20'
            }`}
          >
            {isCompleted ? <Check className="w-3.5 h-3.5" /> : step.number}
          </div>

          {/* Step Info */}
          {!isCollapsed && (
            <div className="flex-1 min-w-0 space-y-0.5">
              <div
                className={`text-xs font-semibold transition-colors ${
                  isActive
                    ? 'text-primary'
                    : isCompleted
                    ? 'text-success'
                    : 'text-foreground'
                }`}
              >
                {step.title}
              </div>
              <div className="text-[10px] text-muted-foreground leading-tight line-clamp-1">
                {step.description}
              </div>
            </div>
          )}

          {/* Status Indicator */}
          {!isCollapsed && (
            <div className="flex-shrink-0">
              {isActive && (
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              )}
              {isCompleted && !isActive && (
                <div className="w-1.5 h-1.5 rounded-full bg-success" />
              )}
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <ApprovalIconButton
          variant="outline"
          size="sm"
          onClick={toggleMobile}
          icon={isMobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          className="bg-card shadow-lg"
        />
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-40 animate-in fade-in duration-200"
          onClick={toggleMobile}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:sticky top-0 left-0 h-screen lg:h-auto
          bg-card border-r border-border
          transition-all duration-300 ease-in-out z-40
          ${isCollapsed ? 'w-16' : 'w-72'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-border p-3">
          <div className="flex items-center justify-between">
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold text-foreground truncate">
                  Approval Stage Setup
                </h3>
                <p className="text-[10px] text-muted-foreground truncate">
                  Configure your workflow
                </p>
              </div>
            )}
            <button
              onClick={toggleCollapse}
              className="hidden lg:flex flex-shrink-0 w-6 h-6 items-center justify-center rounded-md hover:bg-accent transition-colors"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              ) : (
                <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>

        {/* Steps */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {steps.map((step) => (
            <StepContent key={step.number} step={step} />
          ))}

          {/* Progress Summary */}
          {!isCollapsed && (
            <div className="mt-4 p-2.5 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-medium text-muted-foreground">
                  Progress
                </span>
                <span className="text-[10px] font-semibold text-foreground">
                  Step {currentStep} of {steps.length}
                </span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${(completedSteps.size / steps.length) * 100}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                <span className="text-[10px] font-medium text-muted-foreground">
                  Completed Steps
                </span>
                <span className="text-[10px] font-semibold text-success">
                  {completedSteps.size} of {steps.length}
                </span>
              </div>
            </div>
          )}

          {/* Collapsed Progress */}
          {isCollapsed && (
            <div className="flex flex-col items-center gap-1 p-2">
              <div className="text-[10px] font-semibold text-foreground">
                {completedSteps.size}/{steps.length}
              </div>
              <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${(completedSteps.size / steps.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Tip */}
        {!isCollapsed && (
          <div className="flex-shrink-0 border-t border-border p-3">
            <div className="text-[10px] text-muted-foreground leading-relaxed">
              💡 <span className="font-medium">Tip:</span> Complete steps in order for the best
              experience
            </div>
          </div>
        )}
      </div>
    </>
  );
};
